// Request/response transport over a single RTCDataChannel.
//
// Wire format:
//   * string frames are JSON control messages.
//   * binary frames are chunk frames: [id: uint32 LE][bytes...].
//     A binary frame with no payload after the id signals EOF for that id.
//
// Control messages:
//   client → server: { id, type:"get", path:string }
//   server → client: { id, type:"head", status, mime?, size? }
//                  : { id, type:"err",  message:string }
//   server → client (push, no id): { type:"init",    path:string }
//                                : { type:"changed", path:string }

export interface FetchResponse {
  status: number;
  mime: string;
  size: number;
  bytes: Uint8Array;
}

interface Pending {
  resolve: (r: FetchResponse) => void;
  reject: (e: Error) => void;
  head?: { status: number; mime: string; size: number };
  chunks: Uint8Array[];
  received: number;
}

export type ServerPush = { type: string; [k: string]: unknown };

export class Transport {
  private dc: RTCDataChannel;
  private nextId = 1;
  private pending = new Map<number, Pending>();
  private pushHandlers: ((m: ServerPush) => void)[] = [];

  constructor(dc: RTCDataChannel) {
    this.dc = dc;
    this.dc.binaryType = "arraybuffer";
    this.dc.addEventListener("message", (e) => this.onMessage(e));
    this.dc.addEventListener("close", () => this.failAll(new Error("data channel closed")));
    this.dc.addEventListener("error", () => this.failAll(new Error("data channel error")));
  }

  async get(path: string): Promise<FetchResponse> {
    const id = this.nextId++;
    return new Promise<FetchResponse>((resolve, reject) => {
      this.pending.set(id, { resolve, reject, chunks: [], received: 0 });
      this.dc.send(JSON.stringify({ id, type: "get", path }));
    });
  }

  sendControl(m: ServerPush): void {
    this.dc.send(JSON.stringify(m));
  }

  /** Subscribe to server-initiated control messages (no `id` field). */
  onPush(h: (m: ServerPush) => void): void {
    this.pushHandlers.push(h);
  }

  private onMessage(e: MessageEvent): void {
    if (typeof e.data === "string") {
      let parsed: { id?: number; type?: string; [k: string]: unknown };
      try { parsed = JSON.parse(e.data); } catch { return; }
      const id = parsed.id;
      if (typeof id !== "number") {
        if (typeof parsed.type === "string") {
          for (const h of this.pushHandlers) h(parsed as ServerPush);
        }
        return;
      }
      const p = this.pending.get(id);
      if (!p) return;

      if (parsed.type === "head") {
        const status = Number(parsed.status ?? 0);
        const mime = String(parsed.mime ?? "application/octet-stream");
        const size = Number(parsed.size ?? 0);
        p.head = { status, mime, size };
        if (status >= 400 || size === 0) {
          // No bytes will follow; resolve now.
          this.complete(id);
        }
      } else if (parsed.type === "err") {
        p.reject(new Error(String(parsed.message ?? "error")));
        this.pending.delete(id);
      }
      return;
    }

    // Binary frame.
    const buf = e.data as ArrayBuffer;
    if (buf.byteLength < 4) return;
    const dv = new DataView(buf);
    const id = dv.getUint32(0, true);
    const p = this.pending.get(id);
    if (!p) return;

    const payloadLen = buf.byteLength - 4;
    if (payloadLen === 0) {
      this.complete(id);
      return;
    }
    const chunk = new Uint8Array(buf, 4);
    p.chunks.push(chunk);
    p.received += chunk.byteLength;
    if (p.head && p.received >= p.head.size) {
      this.complete(id);
    }
  }

  private complete(id: number): void {
    const p = this.pending.get(id);
    if (!p) return;
    this.pending.delete(id);
    const head = p.head ?? { status: 0, mime: "application/octet-stream", size: 0 };
    const total = p.chunks.reduce((n, c) => n + c.byteLength, 0);
    const bytes = new Uint8Array(total);
    let off = 0;
    for (const c of p.chunks) { bytes.set(c, off); off += c.byteLength; }
    p.resolve({ status: head.status, mime: head.mime, size: head.size, bytes });
  }

  private failAll(err: Error): void {
    for (const p of this.pending.values()) p.reject(err);
    this.pending.clear();
  }
}
